import * as LabelPrimitive from '@radix-ui/react-label';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-ink shadow-card hover:bg-brand/90 active:bg-brand',
  secondary:
    'bg-surface text-ink border border-line shadow-card hover:border-line-strong hover:bg-raised active:bg-sunken',
  ghost: 'text-muted hover:bg-raised hover:text-ink active:bg-sunken',
  danger: 'bg-danger text-white shadow-card hover:bg-danger/90 active:bg-danger',
  subtle: 'bg-brand/10 text-brand hover:bg-brand/[0.16] active:bg-brand/20',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 rounded-md px-2 text-xs gap-1.5',
  sm: 'h-9 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
  icon: 'h-10 w-10',
  'icon-sm': 'h-9 w-9 rounded-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'secondary', size = 'md', type = 'button', loading, icon, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-medium',
        'transition-[background-color,border-color,color,transform] duration-100 active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

// The focus ring lives on the control (not the global :focus-visible outline) so a field
// lights up along its own edge instead of gaining a second, offset halo.
const CONTROL_BASE =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink shadow-card placeholder:text-faint ' +
  'transition-[border-color,box-shadow] duration-100 hover:border-line-strong ' +
  'focus:border-brand focus:outline-none focus:ring-[3px] focus:ring-brand/20 focus-visible:ring-offset-0 ' +
  'disabled:cursor-not-allowed disabled:bg-raised disabled:opacity-70';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(CONTROL_BASE, 'h-10', className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, rows = 4, ...props }, ref) => (
    <textarea ref={ref} rows={rows} className={cn(CONTROL_BASE, 'resize-y leading-relaxed', className)} {...props} />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(CONTROL_BASE, 'h-10 cursor-pointer appearance-none pr-8', className)}
      // The chevron stays in `style` rather than in bg-* utilities: as classes, the arbitrary
      // background-position collides with bg-surface in tailwind-merge and the colour is dropped.
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238a93a2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.6rem center',
        backgroundSize: '16px',
      }}
      {...props}
    />
  ),
);
Select.displayName = 'Select';

export const Label = ({
  className,
  children,
  hint,
  ...props
}: LabelPrimitive.LabelProps & { hint?: string }) => (
  <LabelPrimitive.Root className={cn('field-label', className)} {...props}>
    {children}
    {hint ? <span className="ml-1.5 font-normal normal-case tracking-normal text-faint">{hint}</span> : null}
  </LabelPrimitive.Root>
);

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint/error, so forms line up without repeating markup. */
export const Field = ({ label, hint, error, htmlFor, className, children }: FieldProps) => (
  <div className={cn('min-w-0', className)}>
    {label ? <Label htmlFor={htmlFor}>{label}</Label> : null}
    {children}
    {error ? (
      <p className="mt-1.5 text-xs font-medium text-danger">{error}</p>
    ) : hint ? (
      <p className="hint">{hint}</p>
    ) : null}
  </div>
);

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export const Card = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('rounded-panel border border-line bg-surface shadow-card', className)}
    {...props}
  />
);

export const CardHeader = ({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-3.5',
      className,
    )}
  >
    <div className="min-w-0">
      <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
      {description ? <p className="mt-0.5 text-[12.5px] leading-snug text-muted">{description}</p> : null}
    </div>
    {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
  </div>
);

export const CardBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5', className)} {...props} />
);

export const Divider = ({ className }: { className?: string }) => (
  <hr className={cn('border-line', className)} />
);

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export type BadgeTone = 'neutral' | 'brand' | 'positive' | 'warning' | 'danger' | 'muted';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-raised text-muted border-line',
  brand: 'bg-brand/10 text-brand border-brand/20',
  positive: 'bg-positive/10 text-positive border-positive/20',
  warning: 'bg-warning/10 text-warning border-warning/25',
  danger: 'bg-danger/10 text-danger border-danger/20',
  muted: 'bg-transparent text-faint border-line',
};

export const Badge = ({
  tone = 'neutral',
  className,
  children,
  dot,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
  dot?: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-1.5 py-[3px] text-2xs font-medium leading-none',
      BADGE_TONES[tone],
      className,
    )}
  >
    {dot ? (
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full ring-2"
        style={{ background: dot, ['--tw-ring-color' as string]: `${dot}33` }}
      />
    ) : null}
    {children}
  </span>
);

/**
 * Status pill. This app is mostly about state — a lead, an email, a job and a post all move
 * through stages — so every status reads with the same dot-and-label vocabulary.
 */
export const StatusPill = ({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) => {
  const dot = {
    neutral: 'bg-faint',
    brand: 'bg-brand',
    positive: 'bg-positive',
    warning: 'bg-warning',
    danger: 'bg-danger',
    muted: 'bg-faint/50',
  }[tone];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-1.5 py-[3px] text-2xs font-medium leading-none',
        BADGE_TONES[tone],
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
      {children}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export const Spinner = ({ className }: { className?: string }) => (
  <Loader2 className={cn('h-4 w-4 animate-spin text-faint', className)} aria-label="Loading" />
);

export const LoadingBlock = ({ label = 'Loading…' }: { label?: string }) => (
  <div role="status" className="flex items-center justify-center gap-2 py-16 text-[13px] text-muted">
    <Spinner />
    {label}
  </div>
);

/** Placeholder block for content that is still arriving. */
export const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn('relative overflow-hidden rounded-md bg-raised', className)}>
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-surface/60 to-transparent" />
  </div>
);

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) => (
  <div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
    {icon ? (
      <div className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-raised text-faint">
        {icon}
      </div>
    ) : null}
    <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
    {description ? (
      <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-muted">{description}</p>
    ) : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const ErrorBlock = ({ error, className }: { error: unknown; className?: string }) => (
  <div
    role="alert"
    className={cn(
      'rounded-lg border border-danger/25 bg-danger/[0.07] px-4 py-3 text-[13px] font-medium text-danger',
      className,
    )}
  >
    {error instanceof Error ? error.message : 'Something went wrong'}
  </div>
);

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export const Table = ({ className, ...props }: HTMLAttributes<HTMLTableElement>) => (
  <div className="w-full overflow-x-auto">
    <table className={cn('w-full border-collapse text-left text-[13px]', className)} {...props} />
  </div>
);

export const Th = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <th
    className={cn(
      'sticky top-0 z-10 whitespace-nowrap border-b border-line bg-raised/80 px-3 py-3 text-xs font-medium text-muted backdrop-blur-md first:pl-5 last:pr-5',
      className,
    )}
    {...props}
  />
);

export const Td = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('border-b border-line px-3 py-3.5 align-middle first:pl-5 last:pr-5', className)} {...props} />
);

export const Tr = ({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={cn('transition-colors duration-75 hover:bg-raised/70', className)} {...props} />
);

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export const Switch = ({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) => (
  <label
    className={cn(
      'flex cursor-pointer items-start gap-3',
      disabled && 'cursor-not-allowed opacity-60',
    )}
  >
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors duration-150',
        checked ? 'bg-brand' : 'bg-line-strong',
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-150 ease-out',
          checked ? 'translate-x-[18px]' : 'translate-x-0.5',
        )}
      />
    </button>
    <span className="min-w-0">
      <span className="block text-[13px] font-medium text-ink">{label}</span>
      {description ? <span className="mt-0.5 block text-xs leading-snug text-muted">{description}</span> : null}
    </span>
  </label>
);

export const Progress = ({
  value,
  max = 100,
  color,
  className,
}: {
  value: number;
  max?: number;
  color?: string;
  className?: string;
}) => {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-sunken', className)}>
      <div
        className="h-full rounded-full transition-[width] duration-500 ease-out"
        style={{ width: `${percent}%`, background: color ?? 'rgb(var(--brand))' }}
      />
    </div>
  );
};

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="rounded border border-line bg-raised px-1.5 py-0.5 font-mono text-2xs text-muted shadow-card">
    {children}
  </kbd>
);

export const Mono = ({ children, className }: { children: ReactNode; className?: string }) => (
  <code className={cn('rounded bg-raised px-1.5 py-0.5 font-mono text-[11.5px] text-muted', className)}>
    {children}
  </code>
);
